import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useRobots } from "@/components/seo/Seo";
import { motion } from "framer-motion";
import { BadgeCheck, FileDown, Loader2 } from "lucide-react";
import MilesLogo from "@/components/Logo";

const money = (v, lang) => new Intl.NumberFormat(lang === "en" ? "en-CH" : lang === "de" ? "de-CH" : "fr-CH",
  { style: "currency", currency: "CHF" }).format(v || 0);

const T = {
  fr: { title: "Votre proposition MILES", valid: "Valable jusqu'au", total: "Total proposé",
    accept: "Accepter le devis", name_ph: "Votre nom complet", confirm_accept: "Confirmer l'acceptation",
    accepted_t: "Devis accepté", accepted_d: "Merci — votre trajet est confirmé auprès de nos équipes. Nous revenons vers vous très vite.",
    download: "Télécharger le PDF", expired: "Ce devis n'est plus valable", notfound: "Document introuvable",
    conditions: "Conditions", version: "Version" },
  en: { title: "Your MILES quote", valid: "Valid until", total: "Proposed total",
    accept: "Accept the quote", name_ph: "Your full name", confirm_accept: "Confirm acceptance",
    accepted_t: "Quote accepted", accepted_d: "Thank you — your ride is confirmed with our teams. We'll be in touch shortly.",
    download: "Download the PDF", expired: "This quote is no longer valid", notfound: "Document not found",
    conditions: "Terms", version: "Version" },
  de: { title: "Ihre MILES-Offerte", valid: "Gültig bis", total: "Angebotener Total",
    accept: "Offerte annehmen", name_ph: "Ihr vollständiger Name", confirm_accept: "Annahme bestätigen",
    accepted_t: "Offerte angenommen", accepted_d: "Danke — Ihre Fahrt ist bei unseren Teams bestätigt. Wir melden uns umgehend.",
    download: "PDF herunterladen", expired: "Diese Offerte ist nicht mehr gültig", notfound: "Dokument nicht gefunden",
    conditions: "Bedingungen", version: "Version" },
};

export default function QuotePublic() {
  const { token } = useParams();
  useRobots("noindex, nofollow");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptedAt, setAcceptedAt] = useState(null);

  useEffect(() => {
    axios.get(`/api/public/devis/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) return <Shell><p className="text-center text-slate">Document introuvable.</p></Shell>;
  if (!data) return <Shell><Loader2 className="mx-auto h-6 w-6 animate-spin text-miles" strokeWidth={2} /></Shell>;

  const { quote: q, company } = data;
  const lang = T[q.language] ? q.language : "fr";
  const t = T[lang];
  const accepted = q.status === "accepted" || q.status === "converted" || acceptedAt;
  const expired = q.status === "expired" || q.status === "rejected";

  const accept = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`/api/public/devis/${token}/accept`, { name: name.trim() });
      setAcceptedAt(r.data.accepted_at);
    } catch { setError(true); }
    finally { setBusy(false); }
  };

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-[1.75rem] border border-ink/[0.07] bg-white p-6 shadow-[0_24px_80px_-48px_rgba(16,24,40,0.25)] sm:p-10">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-miles">{t.title}</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-ink sm:text-3xl">{q.number}</h1>
            <p className="mt-1 text-sm text-slate">{q.customer?.name} · {t.version} {q.version || 1}</p>
          </div>
          <span className="rounded-full bg-cloud px-3 py-1.5 text-xs font-medium text-slate">{t.valid} : {q.valid_until}</span>
        </div>

        {q.service_info?.route && (
          <div className="mt-6 rounded-2xl bg-cloud p-4 text-sm">
            <p className="font-semibold text-ink">{q.service_info.route}</p>
            {q.service_info.ride_date && <p className="mt-1 text-slate">{q.service_info.ride_date}{q.service_info.vehicle ? ` · ${q.service_info.vehicle}` : ""}</p>}
          </div>
        )}

        <div className="mt-6 space-y-3">
          {(q.lines || []).map((l, i) => (
            <div key={i} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-ink">{l.description}
                {l.discount_value > 0 && <span className="ml-2 text-xs text-emerald-600">−{l.discount_type === "percent" ? `${l.discount_value} %` : money(l.discount_value, lang)}</span>}
              </span>
              <span className="shrink-0 font-mono font-medium text-ink">{l.unit_price === 0 ? (lang === "fr" ? "Offert" : "Free") : money(l.total, lang)}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between rounded-2xl bg-miles px-5 py-4 text-white">
          <span className="font-semibold">{t.total}</span>
          <span className="font-mono text-xl font-bold" data-testid="public-quote-total">{money(q.total, lang)}</span>
        </div>

        {q.conditions && (
          <div className="mt-6">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate/70">{t.conditions}</p>
            <p className="mt-1.5 text-sm text-slate">{q.conditions}</p>
          </div>
        )}

        <div className="mt-8 border-t border-ink/[0.07] pt-6">
          {accepted ? (
            <div className="flex items-start gap-3 rounded-2xl bg-emerald-50 p-5" data-testid="quote-accepted">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" strokeWidth={1.5} />
              <div>
                <p className="font-semibold text-emerald-800">{t.accepted_t}</p>
                <p className="mt-0.5 text-sm text-emerald-700">{t.accepted_d}</p>
              </div>
            </div>
          ) : expired ? (
            <p className="rounded-2xl bg-cloud p-5 text-center text-sm text-slate" data-testid="quote-expired">{t.expired}</p>
          ) : (
            <div className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.name_ph}
                data-testid="accept-name"
                className="h-14 w-full rounded-2xl border border-ink/10 bg-white px-4 text-[15px] font-medium text-ink outline-none transition-colors placeholder:font-normal placeholder:text-slate/55 focus:border-miles" />
              <button onClick={accept} disabled={!name.trim() || busy} data-testid="accept-button"
                className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-ink text-[15px] font-semibold text-white transition-all hover:bg-miles disabled:opacity-40">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : t.accept}
              </button>
            </div>
          )}
          <a href={`/api/public/devis/${token}/pdf`} data-testid="quote-pdf-download"
            className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-ink/10 text-sm font-medium text-ink transition-colors hover:bg-cloud">
            <FileDown className="h-4 w-4" strokeWidth={1.5} /> {t.download}
          </a>
        </div>

        <p className="mt-8 text-center text-xs text-slate/70">
          {company.brand_name} · {company.email} · {company.phone}
        </p>
      </motion.div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-cloud px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 flex justify-center"><MilesLogo className="h-8" variant="blue" /></div>
        {children}
      </div>
    </div>
  );
}
